import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { 
  ClipboardList, Download, CheckCircle, Clock, AlertTriangle,
  Loader2, RefreshCw, User, Calendar, FileText, ChevronDown,
  ChevronRight, Plus, Edit, Save, HelpCircle, Info, Eye
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { formatBackendDate } from '../../lib/dateUtils';
import InlineDocumentViewer from '../shared/InlineDocumentViewer';
import API_BASE from '../../utils/apiBase';
import {
  resolveInterviewApiRole,
  resolveVacancyJobTitle,
} from '../../utils/interviewRole';

const API = API_BASE;

const DECISION_CONFIG = {
  'Approve': { color: 'bg-green-100 text-green-700', icon: CheckCircle },
  'Hire': { color: 'bg-green-100 text-green-700', icon: CheckCircle },
  'Strong Hire': { color: 'bg-green-100 text-green-700', icon: CheckCircle },
  'On Hold': { color: 'bg-amber-100 text-amber-700', icon: Clock },
  'Consider': { color: 'bg-amber-100 text-amber-700', icon: Clock },
  'Maybe': { color: 'bg-amber-100 text-amber-700', icon: Clock },
  'Reject': { color: 'bg-red-100 text-red-700', icon: AlertTriangle },
  'Not Suitable': { color: 'bg-red-100 text-red-700', icon: AlertTriangle }
};

const INTERVIEW_METHODS = [
  { value: 'phone', label: 'Phone Call' },
  { value: 'video', label: 'Video Call' },
  { value: 'in_person', label: 'In Person' }
];

const getInterviewMethodLabel = (method) => {
  const found = INTERVIEW_METHODS.find((m) => m.value === method);
  return found?.label || (method ? String(method).replace(/_/g, ' ') : 'In Person');
};

const appointmentDateFromScheduledAt = (scheduledAt) => {
  if (!scheduledAt) return new Date().toISOString().split('T')[0];
  try {
    const parsed = new Date(scheduledAt);
    if (Number.isNaN(parsed.getTime())) {
      return String(scheduledAt).split('T')[0];
    }
    return parsed.toISOString().split('T')[0];
  } catch {
    return new Date().toISOString().split('T')[0];
  }
};

const formatAppointmentDisplay = (scheduledAt) => {
  if (!scheduledAt) return '—';
  try {
    return new Date(scheduledAt).toLocaleString('en-GB', {
      weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return scheduledAt;
  }
};

const normalizeInterviewMethod = (method) => {
  const value = String(method || 'video').toLowerCase();
  if (['phone', 'video', 'in_person'].includes(value)) return value;
  if (value.includes('phone')) return 'phone';
  if (value.includes('video')) return 'video';
  return 'in_person';
};

const appointmentFieldsFromSchedule = (appointment) => {
  if (!appointment) return {};
  return {
    interview_date: appointmentDateFromScheduledAt(appointment.scheduled_at),
    interview_method: normalizeInterviewMethod(appointment.method),
    video_meeting_link: appointment.video_meeting_link || '',
    interviewer_name: appointment.interviewer_name || '',
  };
};

// Standard 0-3 scoring scale
const SCORE_OPTIONS = [
  { value: 0, label: '0 - Does not meet criteria', color: 'bg-red-100 text-red-700 border-red-200' },
  { value: 1, label: '1 - Part meets criteria', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  { value: 2, label: '2 - Meets criteria', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  { value: 3, label: '3 - Exceeds criteria', color: 'bg-green-100 text-green-700 border-green-200' }
];

// Score selector component (0-3 scale buttons)
const ScoreSelector = ({ value, onChange, disabled = false }) => (
  <div className="flex gap-1">
    {SCORE_OPTIONS.map(option => (
      <button
        key={option.value}
        type="button"
        onClick={() => !disabled && onChange(option.value)}
        disabled={disabled}
        className={`px-3 py-1.5 text-sm font-medium rounded-lg border transition-all ${
          value === option.value 
            ? option.color + ' ring-2 ring-offset-1' 
            : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
      >
        {option.value}
      </button>
    ))}
  </div>
);

// Question card with scoring criteria tooltip
const QuestionCard = ({ question, score, onChange, notes, onNotesChange, disabled, workerAnswer }) => {
  const [showCriteria, setShowCriteria] = useState(false);
  
  return (
    <div className="border rounded-lg p-4 bg-white space-y-3">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
              Q{question.order}
            </span>
            <span className="text-xs text-gray-400 capitalize">
              {question.category?.replace(/_/g, ' ')}
            </span>
          </div>
          <p className="font-medium text-gray-900 text-sm leading-relaxed">
            {question.question}
          </p>
          {question.skills_assessed && (
            <p className="text-xs text-gray-500 mt-1">
              <span className="font-medium">Assessing:</span> {question.skills_assessed}
            </p>
          )}
        </div>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button 
                type="button"
                onClick={() => setShowCriteria(!showCriteria)}
                className="p-1.5 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600"
              >
                <HelpCircle className="h-4 w-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="left" className="max-w-md p-0">
              <div className="p-3 space-y-2">
                <p className="font-medium text-sm">Scoring Criteria:</p>
                {question.scoring_criteria && Object.entries(question.scoring_criteria).map(([score, text]) => (
                  <div key={score} className="flex gap-2 text-xs">
                    <span className={`px-1.5 py-0.5 rounded font-medium ${
                      score === '0' ? 'bg-red-100 text-red-700' :
                      score === '1' ? 'bg-amber-100 text-amber-700' :
                      score === '2' ? 'bg-blue-100 text-blue-700' :
                      'bg-green-100 text-green-700'
                    }`}>{score}</span>
                    <span className="text-gray-600">{text}</span>
                  </div>
                ))}
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      
      {/* Expanded criteria (toggle) */}
      {showCriteria && question.scoring_criteria && (
        <div className="bg-gray-50 rounded-lg p-3 space-y-1.5 text-xs">
          {Object.entries(question.scoring_criteria).map(([s, text]) => (
            <div key={s} className="flex gap-2">
              <span className={`px-1.5 py-0.5 rounded font-medium shrink-0 ${
                s === '0' ? 'bg-red-100 text-red-700' :
                s === '1' ? 'bg-amber-100 text-amber-700' :
                s === '2' ? 'bg-blue-100 text-blue-700' :
                'bg-green-100 text-green-700'
              }`}>{s}</span>
              <span className="text-gray-600">{text}</span>
            </div>
          ))}
        </div>
      )}
      
      {/* Worker's pre-interview answer (read-only source material) */}
      {workerAnswer && (
        <div className="bg-violet-50 border border-violet-200 rounded-lg p-3">
          <p className="text-xs font-medium text-violet-700 mb-1 flex items-center gap-1">
            <User className="h-3 w-3" />
            Worker's answer:
          </p>
          <p className="text-sm text-violet-900 whitespace-pre-wrap">{workerAnswer}</p>
        </div>
      )}

      <div className="flex items-center justify-between pt-2 border-t">
        <Label className="text-xs text-gray-500">Score:</Label>
        <ScoreSelector value={score} onChange={onChange} disabled={disabled} />
      </div>
      
      <Textarea
        placeholder="Notes for this question (optional)..."
        value={notes || ''}
        onChange={(e) => onNotesChange(e.target.value)}
        disabled={disabled}
        className="min-h-[60px] text-sm"
      />
    </div>
  );
};

export default function InterviewFormPanel({
  employeeId,
  employeeName,
  employee,
  employeeRole,
  employeeJobTitle,
  onComplete,
}) {
  const employeeRecord = employee || {
    role: employeeRole,
    job_title: employeeJobTitle,
  };
  const interviewApiRole = resolveInterviewApiRole(employeeRecord);
  const defaultVacancyTitle = resolveVacancyJobTitle(employeeRecord);
  const { user, token } = useAuth();
  // Role-aware: only admin/super_admin/branch_manager may record or edit
  // interviews. Auditors and other roles see records read-only.
  const canRecordInterview = ['admin', 'super_admin', 'branch_manager'].includes(user?.role || '');
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [downloading, setDownloading] = useState(null);
  // C3F #2/#3: separate admin approval of a submitted assessment (+ controlled override).
  const [approving, setApproving] = useState(null);
  const [overrideById, setOverrideById] = useState({});
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerUrl, setViewerUrl] = useState(null);
  const [viewerTitle, setViewerTitle] = useState('Interview Assessment');
  const [viewerFilename, setViewerFilename] = useState('interview_record.pdf');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [interviewConfig, setInterviewConfig] = useState(null);
  const [activeTab, setActiveTab] = useState('part1');
  const [workerAnswers, setWorkerAnswers] = useState(null); // Worker pre-interview data
  const [prefilled, setPrefilled] = useState(false); // Whether form was prefilled from worker answers
  const [scheduledAppointment, setScheduledAppointment] = useState(null);
  const [appointmentPrefilled, setAppointmentPrefilled] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    interview_date: new Date().toISOString().split('T')[0],
    interview_method: 'in_person',
    interviewer_name: user?.name || '',
    video_meeting_link: '',
    candidate_name: employeeName || '',
    vacancy_job_title: defaultVacancyTitle,
    panel_members: '',
    question_scores: {},  // {question_id: score}
    question_notes: {},   // {question_id: notes}
    // Part 2 - Admin questions
    requires_work_permit: '',
    rtw_proof_taken: '',
    hours_wanted: '',
    flexible_working: '',
    has_driving_licence: '',
    annual_leave_booked: '',
    notice_period: '',
    start_date: '',
    // Decision
    decision: '',
    overall_impression: '',
    candidate_questions: '',
    notes: ''
  });
  const [editingId, setEditingId] = useState(null);

  // Fetch interview configuration (questions based on role)
  const fetchInterviewConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const role = interviewApiRole;
      const response = await axios.get(`${API}/interview-config/${encodeURIComponent(role)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInterviewConfig(response.data);
    } catch (error) {
      console.error('Failed to fetch interview config:', error);
      // Use default config if endpoint fails
      setInterviewConfig({
        questions: [],
        scoring: { scale: { min: 0, max: 3 }, minimum_total_score: 11 },
        administrative_questions: []
      });
    }
  };

  const fetchInterviews = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`${API}/employees/${employeeId}/interview-records`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setInterviews(response.data.records || []);
    } catch (error) {
      console.error('Failed to fetch interview records:', error);
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/employees/${employeeId}/forms`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { requirement_id: 'interview_record' }
        });
        setInterviews(response.data.forms || response.data || []);
      } catch (err) {
        setInterviews([]);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (employeeId) {
      fetchInterviews();
      fetchInterviewConfig();
    }
  }, [employeeId, interviewApiRole]);

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      vacancy_job_title: defaultVacancyTitle,
    }));
  }, [defaultVacancyTitle]);

  // Part 2 field mapping: worker admin_q IDs -> admin interview field IDs
  const WORKER_TO_ADMIN_MAP = {
    admin_q1: 'requires_work_permit',
    admin_q2: 'rtw_proof_taken',
    admin_q3: 'hours_wanted',
    admin_q4: 'flexible_working',
    admin_q5: 'has_driving_licence',
    admin_q6: 'annual_leave_booked',
    admin_q7: 'notice_period',
    admin_q8: 'start_date'
  };

  // Normalise yes_no checkbox values from worker form to admin Select values
  const normaliseYesNo = (val) => {
    if (val === true || val === 'true' || val === 'yes' || val === 'Yes') return 'yes';
    if (val === false || val === 'false' || val === 'no' || val === 'No') return 'no';
    return val || '';
  };

  const fetchScheduledAppointment = async ({ prefill = false } = {}) => {
    if (!employeeId || !token) {
      setScheduledAppointment(null);
      setAppointmentPrefilled(false);
      return null;
    }
    try {
      const response = await axios.get(
        `${API}/employees/${employeeId}/interview-appointment`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const appointment = response.data?.appointment || null;
      setScheduledAppointment(appointment);
      if (prefill && appointment) {
        const scheduleFields = appointmentFieldsFromSchedule(appointment);
        setFormData((prev) => ({
          ...prev,
          ...scheduleFields,
          interviewer_name: scheduleFields.interviewer_name || prev.interviewer_name || user?.name || '',
        }));
        setAppointmentPrefilled(true);
      } else if (!appointment) {
        setAppointmentPrefilled(false);
      }
      return appointment;
    } catch (error) {
      console.error('Failed to fetch scheduled interview:', error);
      setScheduledAppointment(null);
      setAppointmentPrefilled(false);
      return null;
    }
  };

  const openCreateInterviewDialog = async () => {
    resetForm();
    setScheduledAppointment(null);
    setAppointmentPrefilled(false);
    setShowCreateDialog(true);
    await Promise.all([
      fetchWorkerPreInterview(),
      fetchScheduledAppointment({ prefill: true }),
    ]);
  };

  // Fetch worker pre-interview questionnaire and prefill form
  const fetchWorkerPreInterview = async () => {
    try {
      const authToken = token || localStorage.getItem('token');
      const response = await axios.get(
        `${API}/employees/${employeeId}/pre-interview-questionnaire`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      const data = response.data;
      if (data.status === 'not_submitted' || !data.form_data) {
        setWorkerAnswers(null);
        setPrefilled(false);
        return;
      }
      const wd = data.form_data;
      setWorkerAnswers(wd);

      // Prefill Part 2 admin fields from worker's admin_q answers
      const part2Updates = {};
      for (const [workerKey, adminKey] of Object.entries(WORKER_TO_ADMIN_MAP)) {
        if (wd[workerKey] !== undefined && wd[workerKey] !== '' && wd[workerKey] !== null) {
          // yes_no fields need normalisation
          if (['admin_q1', 'admin_q4', 'admin_q5'].includes(workerKey)) {
            part2Updates[adminKey] = normaliseYesNo(wd[workerKey]);
          } else {
            part2Updates[adminKey] = String(wd[workerKey]);
          }
        }
      }

      setFormData(prev => ({ ...prev, ...part2Updates }));
      setPrefilled(true);
    } catch (error) {
      console.error('Failed to fetch worker pre-interview:', error);
      // Non-blocking — form stays blank if fetch fails
      setWorkerAnswers(null);
      setPrefilled(false);
    }
  };

  const resetForm = () => {
    setFormData({
      interview_date: new Date().toISOString().split('T')[0],
      interview_method: 'in_person',
      interviewer_name: user?.name || '',
      video_meeting_link: '',
      candidate_name: employeeName || '',
      vacancy_job_title: defaultVacancyTitle,
      panel_members: '',
      question_scores: {},
      question_notes: {},
      requires_work_permit: '',
      rtw_proof_taken: '',
      hours_wanted: '',
      flexible_working: '',
      has_driving_licence: '',
      annual_leave_booked: '',
      notice_period: '',
      start_date: '',
      decision: '',
      overall_impression: '',
      candidate_questions: '',
      notes: ''
    });
    setEditingId(null);
    setActiveTab('part1');
    setWorkerAnswers(null);
    setPrefilled(false);
    setScheduledAppointment(null);
    setAppointmentPrefilled(false);
  };

  // Calculate total score
  const calculateTotalScore = () => {
    const scores = Object.values(formData.question_scores || {});
    return scores.reduce((sum, s) => sum + (parseInt(s) || 0), 0);
  };

  const getMaxScore = () => {
    return (interviewConfig?.questions?.length || 8) * 3;
  };

  const getPassScore = () => {
    return interviewConfig?.pass_threshold_score
      || interviewConfig?.scoring?.minimum_total_score
      || 11;
  };

  const getInterviewPackLabel = () => {
    return interviewConfig?.role_display_name || 'Support Worker / Care Worker';
  };

  const getInterviewSummaryText = () => {
    const count = interviewConfig?.question_count || interviewConfig?.questions?.length || 8;
    const pass = getPassScore();
    const pack = getInterviewPackLabel();
    const nurseNote = interviewConfig?.is_nurse_role
      ? ' (includes clinical questions)'
      : '';
    return `Interview Assessment — ${pack} — ${count} questions, 0–3 scoring, minimum ${pass} points to pass${nurseNote}`;
  };

  const handleCreateInterview = async (isDraft = false) => {
    if (!formData.decision && !isDraft) {
      toast.error('Please select a decision');
      return;
    }
    
    const totalScore = calculateTotalScore();
    const maxScore = getMaxScore();
    const passScore = getPassScore();
    
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      
      const payload = {
        ...formData,
        total_score: totalScore,
        max_score: maxScore,
        pass_score: passScore,
        passed: totalScore >= passScore,
        percentage: Math.round((totalScore / maxScore) * 100),
        is_draft: isDraft
      };
      
      if (editingId) {
        await axios.put(
          `${API}/employees/${employeeId}/interview-records/${editingId}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success(isDraft ? 'Draft saved' : 'Interview record updated');
      } else {
        await axios.post(
          `${API}/employees/${employeeId}/interview-records`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success(isDraft ? 'Draft saved' : 'Interview record created');
      }
      
      setShowCreateDialog(false);
      resetForm();
      await fetchInterviews();
      if (typeof onComplete === 'function') {
        onComplete();
      }
    } catch (error) {
      console.error('Failed to save interview record:', error);
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to save interview record');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (interview) => {
    const data = interview.form_data || interview.data || {};
    setFormData({
      interview_date: data.interview_date || new Date().toISOString().split('T')[0],
      interview_method: data.interview_method || 'in_person',
      interviewer_name: data.interviewer_name || user?.name || '',
      video_meeting_link: data.video_meeting_link || '',
      candidate_name: data.candidate_name || employeeName || '',
      vacancy_job_title: data.vacancy_job_title || defaultVacancyTitle,
      panel_members: data.panel_members || '',
      question_scores: data.question_scores || {},
      question_notes: data.question_notes || {},
      requires_work_permit: data.requires_work_permit || '',
      rtw_proof_taken: data.rtw_proof_taken || '',
      hours_wanted: data.hours_wanted || '',
      flexible_working: data.flexible_working || '',
      has_driving_licence: data.has_driving_licence || '',
      annual_leave_booked: data.annual_leave_booked || '',
      notice_period: data.notice_period || '',
      start_date: data.start_date || '',
      decision: data.decision || '',
      overall_impression: data.overall_impression || '',
      candidate_questions: data.candidate_questions || '',
      notes: data.notes || ''
    });
    setEditingId(interview.id);
    setShowCreateDialog(true);
  };

  const handleDownloadPDF = async (recordId) => {
    try {
      setDownloading(recordId);
      const token = localStorage.getItem('token');
      
      const response = await axios.get(
        `${API}/form-submissions/${recordId}/download-pdf`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `interview_record_${employeeName?.replace(/\s+/g, '_') || 'employee'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Interview record downloaded');
    } catch (error) {
      console.error('PDF download failed:', error);
      toast.error('Failed to download interview record');
    } finally {
      setDownloading(null);
    }
  };

  const handleViewPDF = (recordId) => {
    if (!recordId) {
      toast.error('Interview record not found');
      return;
    }
    setViewerUrl(`${API}/form-submissions/${recordId}/view-pdf`);
    setViewerTitle('Interview Assessment Record');
    setViewerFilename(`interview_record_${employeeName?.replace(/\s+/g, '_') || 'employee'}.pdf`);
    setViewerOpen(true);
  };

  const getDecisionConfig = (decision) => {
    const key = Object.keys(DECISION_CONFIG).find(k => 
      decision?.toLowerCase().includes(k.toLowerCase())
    );
    return key ? DECISION_CONFIG[key] : { color: 'bg-gray-100 text-gray-600', icon: Clock };
  };

  // C3F #3: final admin approval/rejection of a submitted interview assessment.
  // Distinct from interviewer scoring. Score-derived outcome is the default; an
  // override requires an explicit reason (audited server-side).
  const handleApproveAssessment = async (interviewId, approvalDecision) => {
    if (!interviewId) return;
    const override = overrideById[interviewId] || {};
    const payload = { approval_decision: approvalDecision };
    if (approvalDecision === 'approve' && override.outcome) {
      payload.override_outcome = override.outcome;
      payload.override_reason = override.reason || '';
    }
    setApproving(interviewId);
    try {
      await axios.post(
        `${API}/employees/${employeeId}/interview-records/${interviewId}/approve`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(approvalDecision === 'approve' ? 'Interview assessment approved' : 'Interview assessment rejected');
      await fetchInterviews();
      onComplete?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update the assessment decision');
    } finally {
      setApproving(null);
    }
  };

  const totalScore = calculateTotalScore();
  const maxScore = getMaxScore();
  const passScore = getPassScore();
  const isPassing = totalScore >= passScore;

  // ---- Multi-attempt context (informs the "Record Interview" dialog) ----
  // When admin re-opens "Record Interview" after a failed/rejected attempt,
  // make the lifecycle obvious so they know this is a NEW attempt with a
  // blank scorecard, not an edit of the previous one.
  const sortedInterviews = [...interviews].sort((a, b) => {
    const da = new Date(a?.submitted_at || a?.created_at || 0).getTime();
    const db = new Date(b?.submitted_at || b?.created_at || 0).getTime();
    return db - da;
  });
  const submittedInterviews = sortedInterviews.filter((iv) => (iv?.status || '').toLowerCase() !== 'draft');
  const previousAttempt = !editingId ? submittedInterviews[0] : null;
  const previousAttemptData = previousAttempt?.form_data || previousAttempt?.data || {};
  const previousDecision = previousAttemptData?.decision || previousAttemptData?.overall_decision || null;
  const previousScore = previousAttemptData?.total_score ?? null;
  const previousMax = previousAttemptData?.max_score ?? 24;
  const previousFailed = previousDecision
    ? ['Reject', 'Not Suitable'].includes(previousDecision)
    : (previousScore !== null ? Number(previousScore) < (previousAttemptData?.pass_score || 11) : false);
  const upcomingAttemptNumber = submittedInterviews.length + (editingId ? 0 : 1);

  if (loading) {
    return (
      <Card className="border-[#E4E8EB] shadow-sm">
        <CardContent className="py-8 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
    <Card className="border-[#E4E8EB] shadow-sm">
      <CardHeader>
        <CardTitle className="font-heading text-lg flex items-center justify-between">
          <span className="flex items-center gap-2">
            <ClipboardList className="h-5 w-5 text-primary" />
            Interview Assessment Records (Admin Only)
          </span>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={fetchInterviews}
              disabled={loading}
              className="rounded-xl"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button
              size="sm"
              onClick={openCreateInterviewDialog}
              className="rounded-xl bg-primary hover:bg-primary/90"
              data-testid="create-interview-btn"
              disabled={!canRecordInterview}
              title={canRecordInterview ? 'Record a new interview assessment' : 'Your role does not have permission to record interviews'}
            >
              <Plus className="h-4 w-4 mr-1" />
              {submittedInterviews.length > 0 ? `Record New Interview (Attempt ${upcomingAttemptNumber})` : 'Record Interview'}
            </Button>
          </div>
        </CardTitle>
        <p className="text-sm text-gray-500 mt-1">
          {getInterviewSummaryText()}
        </p>
      </CardHeader>
      <CardContent>
        {interviews.length === 0 ? (
          <div className="rounded-lg border-2 border-dashed border-red-200 bg-red-50 p-6 text-center"
            data-testid="interview-required-banner">
            <AlertTriangle className="h-9 w-9 mx-auto mb-3 text-red-400" />
            <p className="font-semibold text-red-700">Interview Not Conducted</p>
            <p className="text-sm text-red-600 mt-1">
              A documented interview is needed before this file can move forward.
            </p>
            <p className="text-xs text-red-400 mt-1">
              Record the interview to clear this blocker.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {interviews.map((interview, index) => {
              const fData = interview.form_data || {};
              const decision = fData.decision || fData.overall_decision;
              const decisionConfig = getDecisionConfig(decision);
              const DecisionIcon = decisionConfig.icon;
              const isExpanded = expandedId === interview.id;
              const score = fData.total_score || 0;
              const max = fData.max_score || 24;
              const adminDisplayStatus = interview.admin_display_status || (
                decision
                  ? (['Reject', 'Not Suitable'].includes(decision) ? 'reviewed_rejected' : 'reviewed_approved')
                  : interview.status
              );
              const adminDisplayLabel = interview.admin_display_label || (
                adminDisplayStatus === 'under_review' ? 'Completed — assessment under review' :
                adminDisplayStatus === 'reviewed_approved' ? 'Reviewed - approved' :
                adminDisplayStatus === 'reviewed_rejected' ? 'Reviewed - rejected' :
                adminDisplayStatus === 'reviewed_passed' ? 'Reviewed - passed' :
                adminDisplayStatus === 'reviewed_failed' ? 'Reviewed - failed' :
                adminDisplayStatus === 'signed_off' || adminDisplayStatus === 'verified' ? 'Reviewed' :
                String(adminDisplayStatus || 'Submitted, not reviewed').replace(/_/g, ' ')
              );
              
              return (
                <div 
                  key={interview.id || index}
                  className="border rounded-xl overflow-hidden"
                  data-testid={`interview-record-${index}`}
                >
                  <div 
                    className="flex items-center justify-between p-4 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => setExpandedId(isExpanded ? null : interview.id)}
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-primary/10">
                        <ClipboardList className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">
                          Interview Assessment
                        </p>
                        <div className="flex items-center gap-3 text-xs text-gray-500">
                          {fData.interview_date && (
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {formatBackendDate(fData.interview_date)}
                            </span>
                          )}
                          {fData.interview_method && (
                            <span className="flex items-center gap-1">
                              {getInterviewMethodLabel(fData.interview_method)}
                            </span>
                          )}
                          {fData.interviewer_name && (
                            <span className="flex items-center gap-1">
                              <User className="h-3 w-3" />
                              {fData.interviewer_name}
                            </span>
                          )}
                          <span className={`font-medium ${score >= 11 ? 'text-green-600' : 'text-red-600'}`}>
                            Score: {score}/{max}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {/* Outcome status badge (CQC clarity) */}
                      {decision ? (
                        <Badge className={`${decisionConfig.color} flex items-center gap-1`}>
                          <DecisionIcon className="h-3 w-3" />
                          {decision}
                        </Badge>
                      ) : (
                        <Badge className={score >= passScore ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                          {score >= passScore ? 'Completed – Passed' : 'Completed – Failed'}
                        </Badge>
                      )}
                      {interview.status === 'submitted' && adminDisplayStatus !== 'submitted' && (
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                          {adminDisplayLabel}
                        </Badge>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewPDF(interview.id);
                        }}
                        className="rounded-lg"
                        title="View interview PDF"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownloadPDF(interview.id);
                        }}
                        disabled={downloading === interview.id}
                        className="rounded-lg"
                      >
                        {downloading === interview.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Download className="h-4 w-4" />
                        )}
                      </Button>
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4 text-gray-400" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="p-4 border-t bg-white space-y-4">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <p className="text-xs text-gray-500">Method</p>
                          <p className="font-medium">{getInterviewMethodLabel(fData.interview_method)}</p>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <p className="text-xs text-gray-500">Interviewer</p>
                          <p className="font-medium">{fData.interviewer_name || '—'}</p>
                        </div>
                        {fData.video_meeting_link && (
                          <div className="bg-gray-50 p-3 rounded-lg md:col-span-2">
                            <p className="text-xs text-gray-500">Video Link</p>
                            <p className="font-medium text-xs break-all">{fData.video_meeting_link}</p>
                          </div>
                        )}
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <p className="text-xs text-gray-500">Total Score</p>
                          <p className={`font-bold text-lg ${score >= 11 ? 'text-green-600' : 'text-red-600'}`}>
                            {score}/{max}
                          </p>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <p className="text-xs text-gray-500">Percentage</p>
                          <p className="font-bold text-lg">{fData.percentage || Math.round((score/max)*100)}%</p>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <p className="text-xs text-gray-500">Pass Threshold</p>
                          <p className="font-medium">{fData.pass_score || 11} points</p>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <p className="text-xs text-gray-500">Result</p>
                          <Badge className={score >= 11 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                            {score >= 11 ? 'PASSED' : 'FAILED'}
                          </Badge>
                        </div>
                      </div>

                      {fData.overall_impression && (
                        <div>
                          <p className="text-xs font-medium text-gray-500 mb-1">Overall Impression</p>
                          <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded-lg">
                            {fData.overall_impression}
                          </p>
                        </div>
                      )}

                      {fData.notes && (
                        <div>
                          <p className="text-xs font-medium text-gray-500 mb-1">Interviewer Notes</p>
                          <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded-lg">
                            {fData.notes}
                          </p>
                        </div>
                      )}

                      {adminDisplayStatus === 'under_review' && canRecordInterview && (
                        <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 space-y-3" data-testid="interview-approval-panel">
                          <div>
                            <p className="text-sm font-semibold text-blue-900">Assessment awaiting your approval</p>
                            <p className="text-xs text-blue-800 mt-0.5">
                              Score outcome: <span className="font-medium">{(fData.score_outcome === 'passed') ? 'Passed' : (fData.score_outcome === 'not_passed' ? 'Not passed' : (score >= passScore ? 'Passed' : 'Not passed'))}</span>
                              {' '}({score}/{max}, threshold {fData.pass_score || passScore}).
                              Approve to finalise this outcome, or reject to send the assessment back.
                            </p>
                          </div>
                          <div className="text-xs space-y-1">
                            <label className="font-medium text-blue-900">Override outcome (optional)</label>
                            <select
                              className="block w-full rounded-md border border-blue-200 bg-white px-2 py-1 text-sm"
                              value={(overrideById[interview.id]?.outcome) || ''}
                              onChange={(e) => setOverrideById((prev) => ({
                                ...prev,
                                [interview.id]: { ...(prev[interview.id] || {}), outcome: e.target.value },
                              }))}
                              data-testid={`override-outcome-${index}`}
                            >
                              <option value="">Use score-derived outcome</option>
                              <option value="passed">Override: Passed</option>
                              <option value="not_passed">Override: Not passed</option>
                            </select>
                            {overrideById[interview.id]?.outcome ? (
                              <textarea
                                className="mt-1 block w-full rounded-md border border-blue-200 bg-white px-2 py-1 text-sm"
                                rows={2}
                                placeholder="Reason for override (required)"
                                value={(overrideById[interview.id]?.reason) || ''}
                                onChange={(e) => setOverrideById((prev) => ({
                                  ...prev,
                                  [interview.id]: { ...(prev[interview.id] || {}), reason: e.target.value },
                                }))}
                                data-testid={`override-reason-${index}`}
                              />
                            ) : null}
                          </div>
                          <div className="flex flex-wrap gap-2">
                            <Button
                              size="sm"
                              className="bg-green-600 hover:bg-green-700 text-white"
                              onClick={(e) => { e.stopPropagation(); handleApproveAssessment(interview.id, 'approve'); }}
                              disabled={approving === interview.id
                                || (Boolean(overrideById[interview.id]?.outcome) && !(overrideById[interview.id]?.reason || '').trim())}
                              data-testid={`approve-assessment-${index}`}
                            >
                              {approving === interview.id ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Approve assessment'}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-red-300 text-red-700"
                              onClick={(e) => { e.stopPropagation(); handleApproveAssessment(interview.id, 'reject'); }}
                              disabled={approving === interview.id}
                              data-testid={`reject-assessment-${index}`}
                            >
                              Reject assessment
                            </Button>
                          </div>
                        </div>
                      )}

                      <div className="text-xs text-gray-400 pt-2 border-t flex items-center justify-between">
                        <span>Recorded: {formatBackendDate(interview.submitted_at || interview.created_at)}</span>
                        {interview.status === 'draft' && canRecordInterview && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); handleEdit(interview); }}
                            className="rounded-lg"
                          >
                            <Edit className="h-3 w-3 mr-1" />
                            Edit Draft
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>

    {/* Create/Edit Interview Dialog */}
    <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardList className="h-5 w-5 text-primary" />
            {editingId ? 'Edit Interview Assessment Record' : 'Interview Assessment (Final Scored Admin Record)'}
          </DialogTitle>
          <DialogDescription>
            {getInterviewSummaryText()}
          </DialogDescription>
        </DialogHeader>

        {/* Multi-attempt banner — shown when admin opens "Record Interview"
            and a previous attempt exists. Makes it explicit that this is a
            NEW attempt with a blank scorecard, addressing the user concern
            that the form looked like it was loading old answers. */}
        {!editingId && previousAttempt && (
          <div className={`rounded-lg border px-4 py-3 mb-2 ${previousFailed ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
            <div className="flex items-start gap-2">
              <AlertTriangle className={`h-4 w-4 shrink-0 mt-0.5 ${previousFailed ? 'text-red-600' : 'text-amber-600'}`} />
              <div className="text-sm">
                <p className={`font-medium ${previousFailed ? 'text-red-800' : 'text-amber-800'}`}>
                  Recording Interview Attempt #{upcomingAttemptNumber}
                  {previousFailed && ' — previous attempt failed'}
                </p>
                <p className={`text-xs mt-0.5 ${previousFailed ? 'text-red-700' : 'text-amber-700'}`}>
                  Previous attempt: <span className="font-medium">{previousDecision || (previousScore !== null && Number(previousScore) >= 11 ? 'Passed' : 'Failed')}</span>
                  {previousScore !== null && ` (score ${previousScore}/${previousMax})`}.
                  This new record starts with a blank scorecard. Worker pre-interview answers are reference material from their own questionnaire — they are not previous admin scores.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Scheduled interview link banner */}
        {!editingId && appointmentPrefilled && scheduledAppointment && (
          <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 flex items-start gap-2" data-testid="scheduled-interview-linked-banner">
            <Calendar className="h-4 w-4 text-green-600 shrink-0 mt-0.5" />
            <div className="text-sm text-green-800">
              <p className="font-medium">Linked to scheduled interview</p>
              <p className="text-xs mt-0.5 text-green-700">
                {formatAppointmentDisplay(scheduledAppointment.scheduled_at)}
                {' · '}{getInterviewMethodLabel(scheduledAppointment.method)}
                {scheduledAppointment.interviewer_name ? ` · ${scheduledAppointment.interviewer_name}` : ''}
              </p>
              <p className="text-xs mt-0.5 text-green-600">
                Date, method, and interviewer have been prefilled from the appointment. Adjust if the actual interview differed.
              </p>
            </div>
          </div>
        )}
        {!editingId && !scheduledAppointment && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 flex items-start gap-2" data-testid="no-scheduled-interview-banner">
            <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800">
              <span className="font-medium">No scheduled interview found.</span>
              {' '}You can save this assessment as a draft now. Schedule the interview above before submitting the record for review.
            </p>
          </div>
        )}

        {/* Prefill indicator banner */}
        {prefilled && !editingId && (
          <div className="bg-violet-50 border border-violet-200 rounded-lg px-4 py-3 flex items-center gap-2">
            <Info className="h-4 w-4 text-violet-600 shrink-0" />
            <p className="text-sm text-violet-700">
              <span className="font-medium">Prefilled from the worker interview questionnaire.</span>
              {' '}Part 1 shows their written answers. Part 2 admin fields have been pre-populated. All fields remain editable.
            </p>
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid grid-cols-3 mb-4">
            <TabsTrigger value="part1">Part 1: Assessment</TabsTrigger>
            <TabsTrigger value="part2">Part 2: Admin Questions</TabsTrigger>
            <TabsTrigger value="decision">Decision</TabsTrigger>
          </TabsList>

          {/* Part 1 - Assessment Questions */}
          <TabsContent value="part1" className="space-y-4">
            {/* Interview Info Header */}
            <div className="bg-blue-50 rounded-lg p-4 space-y-3">
              <h4 className="font-medium text-blue-800">Interview Details</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <Label className="text-xs">Candidate Name</Label>
                  <Input
                    value={formData.candidate_name}
                    onChange={(e) => setFormData({...formData, candidate_name: e.target.value})}
                    placeholder={employeeName}
                    className="mt-1 bg-white"
                  />
                </div>
                <div>
                  <Label className="text-xs">Job Title</Label>
                  <Input
                    value={formData.vacancy_job_title}
                    onChange={(e) => setFormData({...formData, vacancy_job_title: e.target.value})}
                    className="mt-1 bg-white"
                  />
                </div>
                <div>
                  <Label className="text-xs">Interview Date</Label>
                  <Input
                    type="date"
                    value={formData.interview_date}
                    onChange={(e) => setFormData({...formData, interview_date: e.target.value})}
                    className="mt-1 bg-white"
                  />
                </div>
                <div>
                  <Label className="text-xs">Interviewer Name</Label>
                  <Input
                    value={formData.interviewer_name}
                    onChange={(e) => setFormData({...formData, interviewer_name: e.target.value})}
                    placeholder="Who conducted the interview?"
                    className="mt-1 bg-white"
                  />
                </div>
                <div>
                  <Label className="text-xs">Interview Method</Label>
                  <Select
                    value={formData.interview_method}
                    onValueChange={(v) => setFormData({...formData, interview_method: v})}
                  >
                    <SelectTrigger className="mt-1 bg-white">
                      <SelectValue placeholder="Select method..." />
                    </SelectTrigger>
                    <SelectContent>
                      {INTERVIEW_METHODS.map((m) => (
                        <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Panel Members</Label>
                  <Input
                    value={formData.panel_members}
                    onChange={(e) => setFormData({...formData, panel_members: e.target.value})}
                    placeholder="Names..."
                    className="mt-1 bg-white"
                  />
                </div>
                {formData.interview_method === 'video' && (
                  <div className="md:col-span-2">
                    <Label className="text-xs">Video Meeting Link (optional)</Label>
                    <Input
                      value={formData.video_meeting_link}
                      onChange={(e) => setFormData({...formData, video_meeting_link: e.target.value})}
                      placeholder="e.g. Teams / Zoom link used for the interview"
                      className="mt-1 bg-white"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Score Summary */}
            <div className={`p-3 rounded-lg flex items-center justify-between ${isPassing ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`}>
              <div className="flex items-center gap-3">
                <Info className={`h-5 w-5 ${isPassing ? 'text-green-600' : 'text-amber-600'}`} />
                <span className="text-sm font-medium">
                  Current Score: <span className={isPassing ? 'text-green-700' : 'text-amber-700'}>{totalScore}/{maxScore}</span>
                </span>
              </div>
              <Badge className={isPassing ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                {isPassing ? 'Passing' : `Need ${passScore - totalScore} more points`}
              </Badge>
            </div>

            {/* Assessment Questions */}
            <div className="space-y-4">
              <h4 className="font-medium text-gray-700 border-b pb-2">
                Assessment Questions (Score each 0-3)
              </h4>
              
              {(interviewConfig?.questions || []).map((question) => (
                <QuestionCard
                  key={question.id}
                  question={question}
                  score={formData.question_scores[question.id] ?? null}
                  onChange={(score) => setFormData({
                    ...formData, 
                    question_scores: {...formData.question_scores, [question.id]: score}
                  })}
                  notes={formData.question_notes[question.id] || ''}
                  onNotesChange={(notes) => setFormData({
                    ...formData,
                    question_notes: {...formData.question_notes, [question.id]: notes}
                  })}
                  workerAnswer={!editingId && workerAnswers ? workerAnswers[question.id] : undefined}
                />
              ))}
            </div>
          </TabsContent>

          {/* Part 2 - Administrative Questions */}
          <TabsContent value="part2" className="space-y-4">
            {prefilled && !editingId && (
              <div className="bg-violet-50 border border-violet-200 rounded-lg px-4 py-2 text-sm text-violet-700 flex items-center gap-2">
                <User className="h-4 w-4 text-violet-600 shrink-0" />
                Fields below were pre-populated from the worker's answers. Review and adjust as needed.
              </div>
            )}
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-medium text-gray-700 mb-4">General Interview Questions</h4>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Do you require a Work Permit?</Label>
                    <Select 
                      value={formData.requires_work_permit}
                      onValueChange={(v) => setFormData({...formData, requires_work_permit: v})}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="yes">Yes</SelectItem>
                        <SelectItem value="no">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Proof of RTW taken?</Label>
                    <Input
                      value={formData.rtw_proof_taken}
                      onChange={(e) => setFormData({...formData, rtw_proof_taken: e.target.value})}
                      placeholder="e.g., Passport, BRP, Share Code..."
                      className="mt-1"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>How many hours do you want to work?</Label>
                    <Input
                      value={formData.hours_wanted}
                      onChange={(e) => setFormData({...formData, hours_wanted: e.target.value})}
                      placeholder="e.g., 40 hours/week"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Able to participate in flexible working?</Label>
                    <Select 
                      value={formData.flexible_working}
                      onValueChange={(v) => setFormData({...formData, flexible_working: v})}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="yes">Yes</SelectItem>
                        <SelectItem value="no">No</SelectItem>
                        <SelectItem value="limited">Limited</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Full and valid driver's licence?</Label>
                    <Select 
                      value={formData.has_driving_licence}
                      onValueChange={(v) => setFormData({...formData, has_driving_licence: v})}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="yes_full">Yes - Full Licence</SelectItem>
                        <SelectItem value="yes_provisional">Yes - Provisional</SelectItem>
                        <SelectItem value="no">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Any annual leave booked?</Label>
                    <Input
                      value={formData.annual_leave_booked}
                      onChange={(e) => setFormData({...formData, annual_leave_booked: e.target.value})}
                      placeholder="e.g., None, 2 weeks in August..."
                      className="mt-1"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Notice period required?</Label>
                    <Input
                      value={formData.notice_period}
                      onChange={(e) => setFormData({...formData, notice_period: e.target.value})}
                      placeholder="e.g., 2 weeks, 1 month..."
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>When could you start?</Label>
                    <Input
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Pre-Employment Checks */}
            <div className="bg-amber-50 rounded-lg p-4">
              <h4 className="font-medium text-amber-800 mb-2">Pre-Employment Checks to Complete</h4>
              <ul className="text-sm text-amber-700 space-y-1 list-disc list-inside">
                <li>Explore sickness information on application form</li>
                <li>Check references cover past 2 years with gaps accounted for</li>
                <li>Check Rehabilitation of Offenders Act declaration</li>
                <li>Take copy of eligibility to work in UK documents</li>
                <li>Inform of DBS Check requirement</li>
              </ul>
            </div>
          </TabsContent>

          {/* Decision Tab */}
          <TabsContent value="decision" className="space-y-4">
            {/* Final Score Summary */}
            <div className={`p-4 rounded-lg ${isPassing ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
              <div className="flex items-center justify-between mb-3">
                <h4 className={`font-medium ${isPassing ? 'text-green-800' : 'text-red-800'}`}>
                  Assessment Summary
                </h4>
                <Badge className={isPassing ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                  {isPassing ? 'MEETS THRESHOLD' : 'BELOW THRESHOLD'}
                </Badge>
              </div>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-2xl font-bold">{totalScore}</p>
                  <p className="text-xs text-gray-500">Total Score</p>
                </div>
                <div>
                  <p className="text-2xl font-bold">{maxScore}</p>
                  <p className="text-xs text-gray-500">Maximum</p>
                </div>
                <div>
                  <p className="text-2xl font-bold">{Math.round((totalScore/maxScore)*100)}%</p>
                  <p className="text-xs text-gray-500">Percentage</p>
                </div>
              </div>
            </div>

            <div>
              <Label>Candidate's Questions</Label>
              <Textarea
                value={formData.candidate_questions}
                onChange={(e) => setFormData({...formData, candidate_questions: e.target.value})}
                placeholder="Record any questions the candidate asked and answers given..."
                className="mt-1 min-h-[80px]"
              />
            </div>

            <div>
              <Label>Overall Impression</Label>
              <Textarea
                value={formData.overall_impression}
                onChange={(e) => setFormData({...formData, overall_impression: e.target.value})}
                placeholder="General comments on candidate's suitability, attitude, professionalism..."
                className="mt-1 min-h-[80px]"
              />
            </div>

            {/* Decision */}
            <div className="bg-blue-50 p-4 rounded-lg">
              <Label className="text-blue-800 font-medium">Interview Decision *</Label>
              <Select 
                value={formData.decision}
                onValueChange={(v) => setFormData({...formData, decision: v})}
              >
                <SelectTrigger className="mt-2 bg-white">
                  <SelectValue placeholder="Select decision" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Approve">
                    <span className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-500" /> Successful - Approve
                    </span>
                  </SelectItem>
                  <SelectItem value="On Hold">
                    <span className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-amber-500" /> On Hold - Further Review
                    </span>
                  </SelectItem>
                  <SelectItem value="Reject">
                    <span className="flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-red-500" /> Not Successful - Reject
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Additional Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Any other observations..."
                className="mt-1 min-h-[60px]"
              />
            </div>

            <div>
              <Label>Interviewer Name</Label>
              <Input
                value={formData.interviewer_name}
                onChange={(e) => setFormData({...formData, interviewer_name: e.target.value})}
                placeholder="Your name (for signature)"
                className="mt-1"
              />
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter className="flex gap-2 pt-4 border-t">
          <Button
            variant="outline"
            onClick={() => handleCreateInterview(true)}
            disabled={saving}
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            Save as Draft
          </Button>
          <Button
            onClick={() => handleCreateInterview(false)}
            disabled={saving || !formData.decision}
            className="bg-primary hover:bg-primary/90"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
            Submit Record
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    <InlineDocumentViewer
      open={viewerOpen}
      onClose={() => setViewerOpen(false)}
      fetchUrl={viewerUrl}
      title={viewerTitle}
      token={localStorage.getItem('token')}
      filename={viewerFilename}
    />
    </>
  );
}

